/*
 * CustomerAuthContext — QR menü müşteri auth state.
 *
 * Sorumluluk:
 *   • Sanctum bearer token + cached customer profile'ını localStorage'da tut
 *   • signInWithGoogle / requestPhoneOtp / verifyPhoneOtp / refreshMe /
 *     updateProfile / signOut metodlarını dağıt
 *   • Token rotasyonunda customerClient interceptor'ı ile uyumlu çalış
 *
 * Kullanım:
 *   <CustomerAuthProvider><App/></CustomerAuthProvider>
 *   const { customer, isAuthenticated, signInWithGoogle, ... } = useCustomerAuth();
 *
 * Provider yoksa hook bir "default" bağlam döner — render crash'ini önler
 * (POS sayfaları gibi auth gerekmeyen yerlerde sessiz).
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CustomerUser,
  CustomerProfileUpdatePayload,
  CustomerOtpRequestResponse,
  customerLogout as apiLogout,
  getCustomerMe,
  googleSignIn,
  readCachedCustomer,
  readCustomerToken,
  requestPhoneOtp as apiRequestOtp,
  updateCustomerMe,
  verifyPhoneOtp as apiVerifyOtp,
  writeCachedCustomer,
  writeCustomerToken,
} from '../api/customerAuth';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface CustomerAuthContextValue {
  customer: CustomerUser | null;
  token: string | null;
  isAuthenticated: boolean;
  /** İlk profil hidrasyonu sürerken true. */
  bootstrapping: boolean;
  /** Herhangi bir auth aksiyonu sürerken true. */
  pending: boolean;

  signInWithGoogle: (idToken: string) => Promise<CustomerUser>;
  requestPhoneOtp: (phoneE164: string) => Promise<CustomerOtpRequestResponse>;
  verifyPhoneOtp: (params: {
    phoneE164: string;
    code: string;
    name?: string;
  }) => Promise<CustomerUser>;

  refreshMe: () => Promise<CustomerUser | null>;
  updateProfile: (payload: CustomerProfileUpdatePayload) => Promise<CustomerUser>;
  signOut: () => Promise<void>;
}

const defaultValue: CustomerAuthContextValue = {
  customer: null,
  token: null,
  isAuthenticated: false,
  bootstrapping: false,
  pending: false,
  signInWithGoogle: async () => {
    throw new Error('CustomerAuthProvider altında değil');
  },
  requestPhoneOtp: async () => ({ cooldown_seconds: 0, expires_in_seconds: 0 }),
  verifyPhoneOtp: async () => {
    throw new Error('CustomerAuthProvider altında değil');
  },
  refreshMe: async () => null,
  updateProfile: async () => {
    throw new Error('CustomerAuthProvider altında değil');
  },
  signOut: async () => { },
};

const CustomerAuthContext = createContext<CustomerAuthContextValue>(defaultValue);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ProviderProps {
  children: ReactNode;
}

export const CustomerAuthProvider: React.FC<ProviderProps> = ({ children }) => {
  // İlk render'da localStorage'tan synchron olarak çek — ekran "anonim → giriş
  // yapmış" arasında flash etmesin.
  const [token, setToken] = useState<string | null>(() => readCustomerToken());
  const [customer, setCustomer] = useState<CustomerUser | null>(() =>
    readCachedCustomer(),
  );
  const [bootstrapping, setBootstrapping] = useState<boolean>(() => !!readCustomerToken());
  const [pending, setPending] = useState(false);
  const inFlightMeRef = useRef(0);

  // İlk açılışta token varsa /me ile profilin tazeliğini doğrula. Hata
  // dönerse interceptor zaten 401'de token'ı temizler.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setBootstrapping(false);
      return;
    }
    setBootstrapping(true);
    const ticket = ++inFlightMeRef.current;
    (async () => {
      try {
        const fresh = await getCustomerMe();
        if (cancelled || ticket !== inFlightMeRef.current) return;
        setCustomer(fresh);
        writeCachedCustomer(fresh);
      } catch (err: any) {
        // 401 ise interceptor token'ı silmiştir, biz de state'i nettleştirelim
        if (err?.response?.status === 401) {
          setToken(null);
          setCustomer(null);
        }
      } finally {
        if (!cancelled && ticket === inFlightMeRef.current) {
          setBootstrapping(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Sadece initial token varsa çalışır; signIn akışları kendi state'ini set ediyor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Sign-in: Google -----
  const signInWithGoogle = useCallback(
    async (idToken: string): Promise<CustomerUser> => {
      setPending(true);
      try {
        const res = await googleSignIn(idToken);
        writeCustomerToken(res.token);
        writeCachedCustomer(res.customer);
        setToken(res.token);
        setCustomer(res.customer);
        return res.customer;
      } finally {
        setPending(false);
      }
    },
    [],
  );

  // ----- Sign-in: Phone OTP -----
  const requestPhoneOtp = useCallback(
    async (phoneE164: string): Promise<CustomerOtpRequestResponse> => {
      setPending(true);
      try {
        return await apiRequestOtp(phoneE164);
      } finally {
        setPending(false);
      }
    },
    [],
  );

  const verifyPhoneOtp = useCallback(
    async (params: {
      phoneE164: string;
      code: string;
      name?: string;
    }): Promise<CustomerUser> => {
      setPending(true);
      try {
        const res = await apiVerifyOtp({
          phone: params.phoneE164,
          code: params.code,
          name: params.name,
        });
        writeCustomerToken(res.token);
        writeCachedCustomer(res.customer);
        setToken(res.token);
        setCustomer(res.customer);
        return res.customer;
      } finally {
        setPending(false);
      }
    },
    [],
  );

  // ----- Refresh me -----
  const refreshMe = useCallback(async (): Promise<CustomerUser | null> => {
    const ticket = ++inFlightMeRef.current;
    try {
      const fresh = await getCustomerMe();
      if (ticket === inFlightMeRef.current) {
        setCustomer(fresh);
        writeCachedCustomer(fresh);
      }
      return fresh;
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setToken(null);
        setCustomer(null);
      }
      return null;
    }
  }, []);

  // ----- Update profile -----
  const updateProfile = useCallback(
    async (payload: CustomerProfileUpdatePayload): Promise<CustomerUser> => {
      setPending(true);
      try {
        const fresh = await updateCustomerMe(payload);
        setCustomer(fresh);
        writeCachedCustomer(fresh);
        return fresh;
      } finally {
        setPending(false);
      }
    },
    [],
  );

  // ----- Sign out -----
  const signOut = useCallback(async (): Promise<void> => {
    setPending(true);
    try {
      await apiLogout();
    } finally {
      setToken(null);
      setCustomer(null);
      setPending(false);
    }
  }, []);

  const value = useMemo<CustomerAuthContextValue>(
    () => ({
      customer,
      token,
      isAuthenticated: !!token && !!customer,
      bootstrapping,
      pending,
      signInWithGoogle,
      requestPhoneOtp,
      verifyPhoneOtp,
      refreshMe,
      updateProfile,
      signOut,
    }),
    [
      customer,
      token,
      bootstrapping,
      pending,
      signInWithGoogle,
      requestPhoneOtp,
      verifyPhoneOtp,
      refreshMe,
      updateProfile,
      signOut,
    ],
  );

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useCustomerAuth = (): CustomerAuthContextValue => {
  return useContext(CustomerAuthContext);
};

export default CustomerAuthProvider;
